import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { NAuthClientError, NAuthErrorCode } from '@nauth-toolkit/client';
import { Button } from '../../../components';
import { passwordPolicyIssues } from '../../auth/api';
import { useSignOut } from '../../auth/useAuth';
import { SectionCard } from '../SectionCard';
import { useCanSetPassword, useChangePassword, useSetFirstPassword } from '../useAccount';

const FIELD_CLASS =
  'h-9 rounded-lg border border-line-stronger bg-base px-3 fs-13 text-ink placeholder:text-faint transition-colors focus:border-accent';

function errorMessageFor(error: unknown): string {
  if (!(error instanceof NAuthClientError) || error.isNetworkError) {
    return 'Could not reach the server. Try again.';
  }
  switch (error.code) {
    case NAuthErrorCode.PASSWORD_INCORRECT:
      return 'Current password is incorrect.';
    case NAuthErrorCode.SIGNUP_WEAK_PASSWORD:
      return 'That password does not meet the requirements below.';
    case NAuthErrorCode.PASSWORD_REUSED:
      return 'That was one of your recent passwords. Choose a different one.';
    case NAuthErrorCode.PASSWORD_CHANGE_NOT_ALLOWED:
      // Reachable only if the account gained or lost its password between this
      // screen loading and the form being sent — which form was shown is
      // decided by `canSetPassword` below, and the two routes are exclusive.
      return 'Your sign-in methods changed while this was open. Reload the page and try again.';
    default:
      return error.message;
  }
}

/**
 * Password, in two forms that are never both available.
 *
 * CHANGING one requires the current password — `ChangePasswordDTO.oldPassword`
 * is not optional on the wire, and the API verifies it before touching
 * anything (see PASSWORD_INCORRECT in auth.service.js) — so there is no
 * client-side shortcut past that check even if this form omitted the field.
 *
 * SETTING a first one is for a Google-only account, which has no current
 * password to give. nauth gates that on `canSetPassword` (true only while
 * `passwordHash` is empty) and takes it on a separate route; `POST
 * /auth/social/set-password` then routes through the very same
 * `updateUserPassword` helper, so everything below about sessions applies to it
 * equally.
 *
 * The part that matters most is what happens AFTER success. Confirmed against
 * the running API: both routes revoke every session belonging to the user,
 * including the one the request itself ran on — the very next request with the
 * same access token 401s with `AUTH_SESSION_NOT_FOUND`. nauth gives no option
 * to exempt the caller's session, so "keep the session alive" is not on the
 * table; the only honest choice is the other one the brief names: tell the
 * user plainly and send them to sign in. The success panel below says so
 * explicitly rather than silently bouncing them, an immediate `signOut.mutate()`
 * finishes clearing the now-dead local token and the whole query cache (the
 * same path the sidebar's "Sign out" button uses — see useSignOut), and the
 * redirect is on a short delay so the message is actually readable, with a
 * manual link for anyone who does not want to wait.
 */
export function ChangePasswordSection() {
  const navigate = useNavigate();
  const changePassword = useChangePassword();
  const setFirstPassword = useSetFirstPassword();
  const canSetPassword = useCanSetPassword();
  const signOut = useSignOut();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);

  // Only `true` switches the form to "set a first password": while the answer
  // is still loading the account is assumed to have one, which is the common
  // case, and submitting is held off until it resolves so the form can never
  // send to the wrong route.
  const isFirstPassword = canSetPassword.data === true;
  const pending = changePassword.isPending || setFirstPassword.isPending;
  const error = changePassword.error ?? setFirstPassword.error;

  const policyIssues = passwordPolicyIssues(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    (isFirstPassword || oldPassword !== '') &&
    policyIssues.length === 0 &&
    passwordsMatch &&
    !canSetPassword.isPending;

  // Auto-redirect after the change succeeds, so a user who closes their eyes
  // for a second is not left parked on a screen whose session is already gone.
  // The delay only exists to make the explanation above readable; the "Continue
  // now" link is there for anyone who does not want to wait for it.
  useEffect(() => {
    if (!done) return undefined;
    const timer = setTimeout(() => navigate('/signin', { replace: true }), 2500);
    return () => clearTimeout(timer);
  }, [done, navigate]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    const finish = {
      onSuccess: () => {
        setDone(true);
        signOut.mutate();
      },
    };
    if (isFirstPassword) {
      setFirstPassword.mutate(newPassword, finish);
    } else {
      changePassword.mutate({ oldPassword, newPassword }, finish);
    }
  };

  if (done) {
    return (
      <SectionCard title="Password">
        <p className="fs-13 text-ink-soft">
          {isFirstPassword ? 'Password set.' : 'Password changed.'} For your security every session —
          including this one — has been signed out. Redirecting to sign-in…
        </p>
        <Link to="/signin" className="mt-2 inline-block fs-13 font-semibold text-accent hover:underline">
          Continue to sign in now →
        </Link>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Password"
      description={
        isFirstPassword
          ? 'You sign in with Google. Adding a password gives you a second way in — and signs out every session, including this one.'
          : 'Changing your password signs out every session, including this one.'
      }
    >
      <form onSubmit={handleSubmit} className="flex max-w-sm flex-col gap-3">
        {!isFirstPassword && (
          <label className="flex flex-col gap-1">
            <span className="fs-12 font-medium text-ink-soft">Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={oldPassword}
              onChange={(event) => setOldPassword(event.target.value)}
              className={FIELD_CLASS}
            />
          </label>
        )}

        <label className="flex flex-col gap-1">
          <span className="fs-12 font-medium text-ink-soft">
            {isFirstPassword ? 'Password' : 'New password'}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            className={FIELD_CLASS}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="fs-12 font-medium text-ink-soft">
            {isFirstPassword ? 'Confirm password' : 'Confirm new password'}
          </span>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            className={FIELD_CLASS}
          />
          {!passwordsMatch && confirmPassword !== '' && (
            <span className="fs-11 text-danger">Passwords don&apos;t match.</span>
          )}
        </label>

        {newPassword !== '' && policyIssues.length > 0 && (
          <p className="fs-11 text-muted">Still needs: {policyIssues.join(', ')}.</p>
        )}

        {error !== null && (
          <p role="alert" className="rounded-md bg-danger-tint px-3 py-2 fs-13 text-danger">
            {errorMessageFor(error)}
          </p>
        )}

        <Button
          type="submit"
          variant="secondary"
          disabled={!canSubmit || pending}
          className="mt-1 h-9 self-start px-4"
        >
          {pending
            ? isFirstPassword
              ? 'Setting…'
              : 'Changing…'
            : isFirstPassword
              ? 'Set password'
              : 'Change password'}
        </Button>
      </form>
    </SectionCard>
  );
}
